// ============================================================
// src/components/ThemeSelector.jsx
//
// ── WHAT THIS COMPONENT DOES ─────────────────────────────────
// Visual theme picker used in Settings > Themes section.
//
// Renders 4 clickable theme cards. Each card shows:
//   • A gradient preview panel with the theme name rendered
//     in that theme's specific display font
//   • A 3-colour swatch strip (primary / secondary / accent)
//   • Theme name + one-line description + active badge
//
// On selection:
//   1. Applies the theme instantly (data-theme on <html>)
//   2. Saves to localStorage for persistence across refreshes
//   3. Saves to Supabase user_metadata.theme for cross-device sync
//
// Preview cards always render with their own hardcoded colors
// so they look correct regardless of which theme is currently
// active — all four Google Fonts are loaded upfront in index.css.
// ============================================================

import { useState, useEffect }            from 'react'
import { THEMES, getSavedTheme, saveTheme } from '../lib/themeUtils'
import { supabase }                        from '../lib/supabaseClient'


export default function ThemeSelector() {

  // ── Active theme state ─────────────────────────────────────
  const [activeTheme, setActiveTheme] = useState(getSavedTheme)
  // getSavedTheme passed as an initialiser function so localStorage
  // is only read once on mount

  const [saving,  setSaving]  = useState(false)
  const [saved,   setSaved]   = useState(false)
  const [saveErr, setSaveErr] = useState(null)


  // ── Sync from Supabase on mount ─────────────────────────────
  // If the user last changed their theme on another device,
  // this ensures the correct theme is shown and applied here too.
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      const serverTheme = user.user_metadata?.theme
      if (serverTheme && serverTheme !== getSavedTheme()) {
        // Server has a more recent / different preference — honour it
        setActiveTheme(serverTheme)
        saveTheme(serverTheme)  // apply + persist locally
      }
    })
  }, [])


  // ── Select a theme ─────────────────────────────────────────
  async function handleSelect(themeId) {
    // Immediate local apply — no waiting for Supabase
    setActiveTheme(themeId)
    saveTheme(themeId)

    // Then persist to Supabase for cross-device sync
    setSaving(true)
    setSaved(false)
    setSaveErr(null)

    try {
      const { error } = await supabase.auth.updateUser({
        data: { theme: themeId },
      })
      if (error) throw error
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      // Non-fatal — theme is already applied locally
      console.warn('ThemeSelector: Supabase save failed:', err.message)
      setSaveErr('Theme applied locally but could not sync across devices.')
      setTimeout(() => setSaveErr(null), 4000)
    } finally {
      setSaving(false)
    }
  }


  // ── Render ─────────────────────────────────────────────────
  return (
    <div>

      {/* Theme card grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '0.75rem',
      }}>
        {THEMES.map(theme => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            isActive={activeTheme === theme.id}
            onSelect={handleSelect}
          />
        ))}
      </div>

      {/* Save feedback */}
      {saving && (
        <div className="d-flex align-items-center gap-2 text-muted small">
          <span className="spinner-border spinner-border-sm" />
          Syncing theme…
        </div>
      )}

      {saved && !saving && (
        <div className="small d-flex align-items-center gap-1" style={{ color: 'var(--tertiary-1-dark)' }}>
          <i className="bi bi-check-circle-fill" />
          Theme synced across your devices.
        </div>
      )}

      {saveErr && !saving && (
        <div className="small" style={{ color: 'var(--tertiary-3-dark)' }}>
          <i className="bi bi-exclamation-circle me-1" />
          {saveErr}
        </div>
      )}

    </div>
  )
}


// ─────────────────────────────────────────────────────────────
// ThemeCard
// Individual theme preview card. Uses hardcoded colors from the
// THEMES registry for the gradient and swatches — NOT CSS variables
// — so each card always shows its own palette regardless of the
// currently active theme.
// ─────────────────────────────────────────────────────────────
function ThemeCard({ theme, isActive, onSelect }) {

  const [hovered, setHovered] = useState(false)

  // Card shell styles — elevation increases when active or hovered
  const cardStyle = {
    all:           'unset',
    cursor:        'pointer',
    display:       'block',
    borderRadius:  'var(--radius-lg)',

    // Active: thick themed border + subtle lift
    // Hovered: thin accent border + tiny lift
    // Default: neutral border, flat
    border:        isActive
                     ? '3px solid var(--primary)'
                     : hovered
                       ? '2px solid var(--neutral-border)'
                       : '2px solid var(--secondary-light, #dda0dd)',

    overflow:      'hidden',
    background:    'var(--brand-white)',
    transition:    'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
    boxShadow:     isActive
                     ? 'var(--shadow-pink)'
                     : hovered
                       ? 'var(--shadow-md)'
                       : 'var(--shadow-sm)',
    transform:     isActive || hovered ? 'translateY(-3px)' : 'translateY(0)',
    position:      'relative',
    // Ensure focus ring for keyboard nav
    outline:       'none',
  }

  return (
    <button
      type="button"
      style={cardStyle}
      onClick={() => onSelect(theme.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      aria-pressed={isActive}
      aria-label={`Select ${theme.name} theme${isActive ? ' (currently active)' : ''}`}
    >

      {/* ── Gradient preview panel ──────────────────────────── */}
      {/* Uses theme.previewBg (hardcoded gradient) so each card
          shows its own identity independent of the active theme */}
      <div style={{
        height:          '84px',
        background:      theme.previewBg,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         '0.5rem 0.75rem',
        position:        'relative',
        overflow:        'hidden',
      }}>

        {/* Subtle background orb for depth */}
        <div style={{
          position:     'absolute',
          top:          '-20px',
          right:        '-20px',
          width:        '80px',
          height:       '80px',
          borderRadius: '50%',
          background:   'rgba(255,255,255,0.08)',
          pointerEvents: 'none',
        }} />

        {/* Theme name in its own font */}
        <span style={{
          fontFamily:   `'${theme.font}', sans-serif`,
          fontWeight:   theme.fontWeight,
          fontSize:     theme.id === 'its-pink' ? '1.5rem' : '1.75rem',
          // Teko, Anton are condensed and benefit from slightly larger size
          color:        theme.previewText,
          textShadow:   '0 1px 4px rgba(0,0,0,0.45)',
          letterSpacing: theme.id === 'blue-shock' ? '0.03em' : '0.01em',
          lineHeight:   1,
          textAlign:    'center',
          position:     'relative',
          zIndex:       1,
        }}>
          {theme.name}
        </span>
      </div>


      {/* ── Card footer ───────────────────────────────────────── */}
      <div style={{ padding: '0.6rem 0.85rem 0.7rem' }}>

        {/* Name row + active badge */}
        <div style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          marginBottom:   '0.2rem',
          gap:            '0.35rem',
        }}>
          <span style={{
            fontWeight:  800,
            fontSize:    '0.88rem',
            color:       'var(--neutral-1)',
            lineHeight:  1.2,
          }}>
            {theme.name}
          </span>

          {isActive && (
            <span style={{
              background:    'var(--primary)',
              color:         'white',
              borderRadius:  '999px',
              fontSize:      '0.6rem',
              fontWeight:    800,
              padding:       '0.12rem 0.45rem',
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              flexShrink:    0,
              lineHeight:    1.5,
            }}>
              Active
            </span>
          )}
        </div>

        {/* One-line description */}
        <div style={{
          fontSize:   '0.72rem',
          color:      'var(--neutral-1)',
          opacity:    0.6,
          lineHeight: 1.4,
          marginBottom: '0.25rem',
        }}>
          {theme.description}
        </div>

        {/* Font credit */}
        <div style={{
          fontSize:   '0.65rem',
          color:      'var(--neutral-1)',
          opacity:    0.4,
          fontStyle:  'italic',
        }}>
          Font: {theme.font}
        </div>
      </div>

    </button>
  )
}